import { useEffect, useState, useRef } from "react";
import {
  useAdminPlans,
  useAdminPlanModels,
  useAdminCodecraftModels,
  useAdminPlanUsers,
  useAdminCreatePlan,
  useAdminUpdatePlan,
  useAdminDeletePlan,
} from "../hooks/useApi";
import { formatNumber, formatTomanInt, toPersianDigits } from "../utils/format";
import "../admin-theme.css";

const DURATION_OPTIONS = [
  { days: 30, label: "۳۰ روزه" },
  { days: 90, label: "۳ ماهه (۹۰ روز)" },
  { days: 180, label: "۶ ماهه (۱۸۰ روز)" },
  { days: 365, label: "۱ ساله (۳۶۵ روز)" },
];

interface PlanForm {
  plan_id?: number;
  name: string;
  plan_kind: "pay_as_you_go" | "fixed";
  monthly_price: string;
  fixed_price_usd: string;
  platform_fee: string;
  caching_discount: string;
  priority_level: string;
  durations: number[];
  discounts: Record<number, number>; // duration (days) -> discount percent
  model_ids: number[]; // PAYG: OpenRouter AIModel ids
  fixed_model_ids: string[]; // fixed: CodeCraft model ids
  is_active: boolean;
  is_public: boolean;
  visible_user_ids: number[];
  requests_per_minute: string;
  tokens_per_minute: string;
  monthly_token_limit: string;
  max_concurrent_requests: string;
  fixed_models_text: string; // comma/newline separated extra CodeCraft model ids
}

const EMPTY_FORM: PlanForm = {
  name: "",
  plan_kind: "pay_as_you_go",
  monthly_price: "",
  fixed_price_usd: "",
  platform_fee: "0",
  caching_discount: "0",
  priority_level: "0",
  durations: [],
  discounts: {},
  model_ids: [],
  fixed_model_ids: [],
  is_active: true,
  is_public: true,
  visible_user_ids: [],
  requests_per_minute: "",
  tokens_per_minute: "",
  monthly_token_limit: "",
  max_concurrent_requests: "",
  fixed_models_text: "",
};

export default function AdminPlans() {
  const { data: plans, isLoading } = useAdminPlans();
  const { data: allModels } = useAdminPlanModels();
  const createPlan = useAdminCreatePlan();
  const updatePlan = useAdminUpdatePlan();
  const deletePlan = useAdminDeletePlan();

  const [showForm, setShowForm] = useState(false);
  const overlayMouseDown = useRef(false);
  const [form, setForm] = useState<PlanForm>(EMPTY_FORM);
  const [userSearch, setUserSearch] = useState("");
  const [debouncedUserSearch, setDebouncedUserSearch] = useState("");

  const { data: codecraftModels, error: codecraftError, isPending: codecraftLoading } =
    useAdminCodecraftModels(Boolean(showForm && form.plan_kind === "fixed"));

  useEffect(() => {
    const t = setTimeout(() => setDebouncedUserSearch(userSearch.trim()), 300);
    return () => clearTimeout(t);
  }, [userSearch]);

  const { data: planUsers } = useAdminPlanUsers(debouncedUserSearch, form.visible_user_ids);

  const openCreate = () => {
    setForm({ ...EMPTY_FORM, durations: [30, 90, 180, 365] });
    setShowForm(true);
  };

  const openEdit = (plan: Record<string, unknown>) => {
    const rawDiscounts = (plan.duration_discounts as Record<string, number> | undefined) || {};
    const discounts: Record<number, number> = {};
    for (const [k, v] of Object.entries(rawDiscounts)) {
      const days = Number(k);
      if (!Number.isNaN(days)) discounts[days] = Number(v) || 0;
    }
    setForm({
      plan_id: plan.plan_id as number,
      name: plan.name as string,
      plan_kind: (plan.plan_kind as "pay_as_you_go" | "fixed" | undefined) === "fixed" ? "fixed" : "pay_as_you_go",
      monthly_price: plan.plan_kind === "fixed" ? "" : String((Number(plan.monthly_price) || 0) / 10),
      fixed_price_usd: String(plan.fixed_price_usd ?? (plan.plan_kind === "fixed" ? plan.monthly_price : "")),
      platform_fee: String(plan.platform_fee ?? "0"),
      caching_discount: String(plan.caching_discount ?? "0"),
      priority_level: String(plan.priority_level ?? "0"),
      durations: (plan.available_durations as number[]) || [],
      discounts,
      model_ids: (plan.available_model_ids as number[]) || [],
      fixed_model_ids: ((plan.fixed_models as string[] | undefined) || []).slice(),
      is_active: Boolean(plan.is_active),
      is_public: plan.is_public !== false,
      visible_user_ids: (plan.visible_user_ids as number[]) || [],
      requests_per_minute: String(plan.requests_per_minute ?? ""),
      tokens_per_minute: String(plan.tokens_per_minute ?? ""),
      monthly_token_limit: String(plan.monthly_token_limit ?? ""),
      max_concurrent_requests: String(plan.max_concurrent_requests ?? ""),
      fixed_models_text: "",
    });
    setShowForm(true);
  };

  const toggleDuration = (days: number) => {
    setForm((f) => {
      const enabled = f.durations.includes(days);
      const durations = enabled
        ? f.durations.filter((d) => d !== days)
        : [...f.durations, days];
      const discounts = { ...f.discounts };
      if (enabled) delete discounts[days];
      return { ...f, durations, discounts };
    });
  };

  const setDiscount = (days: number, value: number) => {
    setForm((f) => ({
      ...f,
      discounts: { ...f.discounts, [days]: Math.max(0, Math.min(100, value || 0)) },
    }));
  };

  const toggleModel = (id: number) => {
    setForm((f) => ({
      ...f,
      model_ids: f.model_ids.includes(id)
        ? f.model_ids.filter((m) => m !== id)
        : [...f.model_ids, id],
    }));
  };

  const toggleCodecraftModel = (id: string) => {
    setForm((f) => ({
      ...f,
      fixed_model_ids: f.fixed_model_ids.includes(id)
        ? f.fixed_model_ids.filter((m) => m !== id)
        : [...f.fixed_model_ids, id],
    }));
  };

  const togglePlanUser = (id: number) => {
    const isSelected = form.visible_user_ids.includes(id);
    setForm((f) => ({
      ...f,
      visible_user_ids: isSelected
        ? f.visible_user_ids.filter((x) => x !== id)
        : [...f.visible_user_ids, id],
    }));
    // Clear search after picking a user so the dropdown resets and the
    // next search (partial email/phone/username) filters correctly.
    // Without this the input keeps the previous term and the user has to
    // manually delete it; with the previous backend `OR` logic the empty
    // state returned only selected users so the list appeared broken.
    if (!isSelected) {
      setUserSearch("");
    }
  };

  const userLabel = (id: number) => {
    const u = (planUsers as Record<string, unknown>[] | undefined)?.find((x) => x.id === id);
    if (!u) return `#${id}`;
    const display = (u.name as string) || (u.username as string) || "";
    const email = (u.email as string) || "";
    const phone = (u.phone as string) || "";
    const contact = email && phone ? `${email} / ${phone}` : email || phone;
    if (display && contact) return `${display} (${contact})`;
    return contact || display || `#${id}`;
  };

  const planUserCandidates = ((planUsers as Record<string, unknown>[] | undefined) || []).filter(
    (u) => !form.visible_user_ids.includes(u.id as number)
  );

  // Only active models should be selectable in the plan form.
  // Backend now filters to active only; this client-side filter is a
  // defense-in-depth guard for cached / stale responses.
  const activeModels = ((allModels as Record<string, unknown>[] | undefined) || []).filter(
    (m) => !m.status || m.status === "active"
  );

  const handleSave = () => {
    if (!form.name.trim() || (form.plan_kind === "fixed" ? form.fixed_price_usd === "" : form.monthly_price === "")) return;
    const isFixed = form.plan_kind === "fixed";
    if (isFixed) {
      const missing = [
        form.requests_per_minute,
        form.tokens_per_minute,
        form.monthly_token_limit,
      ].some((v) => Number(v) <= 0);
      if (Number(form.fixed_price_usd) <= 0 || missing) {
        window.alert("برای پلن تعرفه ثابت، قیمت ماهانه و هر سه سقف (درخواست/توکن در دقیقه و توکن ماهانه) الزامی است");
        return;
      }
    }
    const fixedModels = isFixed
      ? Array.from(
          new Set([
            ...form.fixed_model_ids,
            ...form.fixed_models_text
              .split(/[،,.\n]+/)
              .map((s) => s.trim())
              .filter(Boolean),
          ])
        )
      : null;
    const payload = {
      name: form.name.trim(),
      plan_kind: form.plan_kind,
      monthly_price: isFixed ? undefined : (Number(form.monthly_price) || 0) * 10,
      fixed_price_usd: isFixed ? Number(form.fixed_price_usd) : null,
      platform_fee: Number(form.platform_fee) || 0,
      caching_discount: Number(form.caching_discount) || 0,
      priority_level: Number(form.priority_level) || 0,
      available_durations: form.durations,
      duration_discounts: form.discounts,
      available_model_ids: form.model_ids,
      is_active: form.is_active,
      is_public: form.is_public,
      visible_user_ids: form.visible_user_ids,
      requests_per_minute: isFixed ? Number(form.requests_per_minute) : null,
      tokens_per_minute: isFixed ? Number(form.tokens_per_minute) : null,
      monthly_token_limit: isFixed ? Number(form.monthly_token_limit) : null,
      max_concurrent_requests: isFixed ? (Number(form.max_concurrent_requests) || null) : null,
      fixed_models: fixedModels,
    };
    if (form.plan_id != null) {
      updatePlan.mutate({ id: String(form.plan_id), data: payload });
    } else {
      createPlan.mutate(payload);
    }
    setShowForm(false);
  };

  const handleDelete = (plan: Record<string, unknown>) => {
    if (!window.confirm(`پلن «${plan.name}» حذف شود؟`)) return;
    deletePlan.mutate(String(plan.plan_id));
  };

  const modelName = (id: number) => {
    const m = (allModels as Record<string, unknown>[] | undefined)?.find((x) => x.id === id);
    return m ? (m.display_name as string) || (m.model_name as string) : `#${id}`;
  };

  const planModelNames = (plan: Record<string, unknown>) => {
    const ids = (plan.available_model_ids as number[]) || [];
    return ids.map(modelName);
  };

  return (
    <div className="space-y-6">
      {/* Page Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold" style={{ color: "var(--admin-text-primary)" }}>اشتراک‌ها</h1>
          <p className="text-sm mt-1" style={{ color: "var(--admin-text-secondary)" }}>
            تعریف و مدیریت پلن‌های اشتراک، قیمت‌ها و دسترسی به مدل‌ها
          </p>
        </div>
        <button className="admin-btn admin-btn-primary" onClick={openCreate}>
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
            <line x1="12" y1="5" x2="12" y2="19" />
            <line x1="5" y1="12" x2="19" y2="12" />
          </svg>
          پلن جدید
        </button>
      </div>

      {/* Plans Grid */}
      <div className="admin-card overflow-hidden">
        <div className="admin-card-header">
          <h2 className="admin-card-title">لیست پلن‌ها</h2>
        </div>

        {isLoading ? (
          <div className="flex items-center justify-center py-16">
            <svg className="animate-spin h-8 w-8" viewBox="0 0 24 24" fill="none" style={{ color: "var(--admin-primary)" }}>
              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
            </svg>
          </div>
        ) : !plans?.length ? (
          <div className="flex flex-col items-center justify-center py-16" style={{ color: "var(--admin-text-tertiary)" }}>
            <p className="text-sm">هیچ پلنی تعریف نشده است</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4 p-6">
            {(plans as Record<string, unknown>[]).map((plan) => {
              const models = planModelNames(plan);
              const modelChips =
                plan.plan_kind === "fixed"
                  ? ((plan.fixed_models as string[] | undefined) || []).map((id) => String(id))
                  : models;
              const modelsLabel =
                plan.plan_kind === "fixed" ? "مدل‌های CodeCraft" : "مدل‌های فعال";
              return (
                <div
                  key={plan.plan_id as string}
                  className="rounded-2xl p-5 border flex flex-col"
                  style={{ background: "var(--admin-bg-surface)", borderColor: "var(--admin-border)" }}
                >
                  <div className="flex flex-col gap-2 sm:flex-row sm:items-start sm:justify-between mb-2">
                    <div className="min-w-0">
                      <div className="flex flex-wrap items-center gap-2">
                        <h3 className="text-base font-semibold" style={{ color: "var(--admin-text-primary)" }}>
                          {plan.name as string}
                        </h3>
                        {plan.plan_kind === "fixed" ? (
                          <span className="admin-badge" style={{ fontSize: 10, background: "#10b981", color: "#fff" }}>
                            تعرفه ثابت
                          </span>
                        ) : (
                          <span className="admin-badge" style={{ fontSize: 10 }}>مصرفی</span>
                        )}
                        {Boolean(plan.is_active) ? (
                          <span className="admin-badge admin-badge-success" style={{ fontSize: 10 }}>فعال</span>
                        ) : (
                          <span className="admin-badge" style={{ fontSize: 10 }}>غیرفعال</span>
                        )}
                        {plan.is_public === false ? (
                          <span className="admin-badge" style={{ fontSize: 10, background: "var(--admin-error-bg)", color: "var(--admin-error)" }}>
                            خصوصی
                          </span>
                        ) : (
                          <span className="admin-badge" style={{ fontSize: 10 }}>عمومی</span>
                        )}
                      </div>
                      <p className="text-xs mt-0.5" style={{ color: "var(--admin-text-tertiary)" }}>
                        اولویت {toPersianDigits(String(plan.priority_level ?? "0"))}
                      </p>
                    </div>
                    <div className="flex items-center gap-1.5 w-full sm:w-auto shrink-0">
                      <button className="admin-btn admin-btn-secondary admin-btn-sm flex-1 sm:flex-none justify-center whitespace-nowrap" onClick={() => openEdit(plan)}>
                        ویرایش
                      </button>
                      <button
                        className="admin-btn admin-btn-sm flex-1 sm:flex-none justify-center whitespace-nowrap"
                        style={{ background: "var(--admin-error-bg)", color: "var(--admin-error)", border: "none" }}
                        onClick={() => handleDelete(plan)}
                      >
                        حذف
                      </button>
                    </div>
                  </div>

                  <div className="flex items-end justify-between my-3 pb-3 border-b" style={{ borderColor: "var(--admin-border)" }}>
                    <div>
                      <p className="text-[11px]" style={{ color: "var(--admin-text-tertiary)" }}>قیمت ماهانه</p>
                      <p className="text-xl font-bold" style={{ color: "var(--admin-primary)" }}>
                        {plan.plan_kind === "fixed" ? `$${Number(plan.fixed_price_usd ?? plan.monthly_price).toFixed(2)}` : formatTomanInt(plan.monthly_price as string)}{" "}
                        <span className="text-xs font-medium" style={{ color: "var(--admin-text-tertiary)" }}>{plan.plan_kind === "fixed" ? "USD" : "تومان"}</span>
                      </p>
                    </div>
                    {plan.plan_kind !== "fixed" && (
                      <div className="text-left" dir="ltr">
                        <p className="text-[11px]" style={{ color: "var(--admin-text-tertiary)" }}>
                          کارمزد پلتفرم
                        </p>
                        <p className="text-sm font-semibold" style={{ color: "var(--admin-text-primary)" }}>
                          {Math.round(Number(plan.platform_fee) || 0).toLocaleString("fa-IR")}٪
                        </p>
                      </div>
                    )}
                  </div>

                  {/* Durations */}
                  <p className="text-[11px] mb-1.5" style={{ color: "var(--admin-text-tertiary)" }}>مدت‌های قابل خرید</p>
                  <div className="flex flex-wrap gap-1.5 mb-3">
                    {((plan.available_durations as number[]) || []).map((d) => {
                      const discount = Number((plan.duration_discounts as Record<string, number> | undefined)?.[String(d)] || 0);
                      return (
                        <span key={d} className="inline-flex items-center gap-1">
                          <span
                            className="admin-badge"
                            style={{ background: "var(--admin-primary-light)", color: "var(--admin-primary-dark)", fontSize: 11 }}
                          >
                            {DURATION_OPTIONS.find((o) => o.days === d)?.label || `${toPersianDigits(String(d))} روز`}
                          </span>
                          {discount > 0 && (
                            <span
                              className="px-1.5 py-0.5 rounded-md text-[10px] font-semibold"
                              style={{ background: "var(--admin-error-bg)", color: "var(--admin-error)" }}
                            >
                              ٪{toPersianDigits(String(discount))} تخفیف
                            </span>
                          )}
                        </span>
                      );
                    })}
                  </div>

                  {/* Caching discount */}
                  {plan.plan_kind !== "fixed" && (
                    <div className="flex items-center justify-between text-xs mb-2">
                      <span style={{ color: "var(--admin-text-secondary)" }}>تخفیف کش</span>
                      <span className="font-semibold" style={{ color: "var(--admin-primary-dark)" }}>
                        ٪{toPersianDigits(String(plan.caching_discount ?? "0"))}
                      </span>
                    </div>
                  )}

                  {plan.plan_kind === "fixed" && (
                    <div className="rounded-xl px-3 py-2 mb-2 text-xs" style={{ background: "rgba(16,185,129,0.10)", border: "1px solid #10b981" }}>
                      <div className="flex items-center justify-between">
                        <span style={{ color: "var(--admin-text-secondary)" }}>درخواست/دقیقه</span>
                        <span className="font-semibold" style={{ color: "#047857" }}>
                          {toPersianDigits(String(plan.requests_per_minute ?? "۰"))}
                        </span>
                      </div>
                      <div className="flex items-center justify-between mt-1">
                        <span style={{ color: "var(--admin-text-secondary)" }}>توکن/دقیقه</span>
                        <span className="font-semibold" style={{ color: "#047857" }}>
                          {formatNumber(String(plan.tokens_per_minute ?? ""))}
                        </span>
                      </div>
                      <div className="flex items-center justify-between mt-1">
                        <span style={{ color: "var(--admin-text-secondary)" }}>توکن ماهانه</span>
                        <span className="font-semibold" style={{ color: "#047857" }}>
                          {formatNumber(String(plan.monthly_token_limit ?? ""))}
                        </span>
                      </div>
                    </div>
                  )}

                  {/* Models */}
                  <div className="text-xs mb-1" style={{ color: "var(--admin-text-secondary)" }}>
                    {modelsLabel} ({toPersianDigits(String(modelChips.length))})
                  </div>
                  <div className="flex flex-wrap gap-1.5 text-[11px]">
                    {modelChips.length === 0 ? (
                      <span style={{ color: "var(--admin-text-tertiary)" }}>بدون مدل</span>
                    ) : (
                      modelChips.slice(0, 4).map((m) => (
                        <span
                          key={m}
                          className="px-2 py-0.5 rounded-md truncate max-w-[120px]"
                          style={{ background: "var(--admin-bg-hover)", color: "var(--admin-text-secondary)" }}
                          title={m}
                        >
                          {m}
                        </span>
                      ))
                    )}
                    {modelChips.length > 4 && (
                      <span style={{ color: "var(--admin-text-tertiary)" }}>+{toPersianDigits(String(modelChips.length - 4))}</span>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Create / Edit Modal */}
      {showForm && (
        <div
          className="admin-overlay"
          onMouseDown={(e) => { overlayMouseDown.current = e.target === e.currentTarget; }}
          onClick={(e) => { if (e.target === e.currentTarget && overlayMouseDown.current) setShowForm(false); overlayMouseDown.current = false; }}
        >
          <div
            className="admin-glass admin-modal"
            style={{ maxWidth: 640, maxHeight: "90vh", overflowY: "auto" }}
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between mb-5">
              <h2 className="text-base font-semibold" style={{ color: "var(--admin-text-primary)" }}>
                {form.plan_id != null ? "ویرایش پلن" : "پلن جدید"}
              </h2>
              <button
                onClick={() => setShowForm(false)}
                className="p-1 rounded-lg transition-colors"
                style={{ color: "var(--admin-text-tertiary)" }}
              >
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
                  <line x1="18" y1="6" x2="6" y2="18" />
                  <line x1="6" y1="6" x2="18" y2="18" />
                </svg>
              </button>
            </div>

            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium mb-1.5" style={{ color: "var(--admin-text-secondary)" }}>
                  نام پلن
                </label>
                <input
                  type="text"
                  value={form.name}
                  onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
                  className="admin-input"
                  placeholder="مثلاً Pro"
                />
              </div>

              <div>
                <label className="block text-sm font-medium mb-1.5" style={{ color: "var(--admin-text-secondary)" }}>
                  نوع پلن
                </label>
                <div className="flex items-center gap-4 rounded-xl p-3" style={{ background: "var(--admin-bg-hover)", border: "1px solid var(--admin-border)" }}>
                  <label className="flex items-center gap-2.5 cursor-pointer text-sm" style={{ color: "var(--admin-text-primary)" }}>
                    <input
                      type="radio"
                      name="plan-kind"
                      checked={form.plan_kind === "pay_as_you_go"}
                      onChange={() => setForm((f) => ({ ...f, plan_kind: "pay_as_you_go" }))}
                      style={{ accentColor: "var(--admin-primary)" }}
                    />
                    پرداخت بر اساس مصرف (OpenRouter)
                  </label>
                  <label className="flex items-center gap-2.5 cursor-pointer text-sm" style={{ color: "var(--admin-text-primary)" }}>
                    <input
                      type="radio"
                      name="plan-kind"
                      checked={form.plan_kind === "fixed"}
                      onChange={() => setForm((f) => ({ ...f, plan_kind: "fixed" }))}
                      style={{ accentColor: "#10b981" }}
                    />
                    تعرفه ثابت (CodeCraft)
                  </label>
                </div>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium mb-1.5" style={{ color: "var(--admin-text-secondary)" }}>
                    قیمت ماهانه ({form.plan_kind === "fixed" ? "USD" : "تومان"})
                  </label>
                  <input
                    type="number"
                    min={0}
                    step={form.plan_kind === "fixed" ? "0.01" : "1"}
                    value={form.plan_kind === "fixed" ? form.fixed_price_usd : form.monthly_price}
                    onChange={(e) => setForm((f) => form.plan_kind === "fixed" ? ({ ...f, fixed_price_usd: e.target.value }) : ({ ...f, monthly_price: e.target.value }))}
                    className="admin-input"
                    placeholder={form.plan_kind === "fixed" ? "10.00" : "900000"}
                    dir="ltr"
                  />
                </div>
                {form.plan_kind === "pay_as_you_go" && (
                  <div>
                    <label className="block text-sm font-medium mb-1.5" style={{ color: "var(--admin-text-secondary)" }}>
                      کارمزد پلتفرم
                    </label>
                    <input
                      type="number"
                      step="any"
                      min={0}
                      value={form.platform_fee}
                      onChange={(e) => setForm((f) => ({ ...f, platform_fee: e.target.value }))}
                      className="admin-input"
                      placeholder="1.1"
                      dir="ltr"
                    />
                  </div>
                )}
                {form.plan_kind === "pay_as_you_go" && (
                  <div>
                    <label className="block text-sm font-medium mb-1.5" style={{ color: "var(--admin-text-secondary)" }}>
                      تخفیف کش (٪)
                    </label>
                    <input
                      type="number"
                      min={0}
                      max={100}
                      value={form.caching_discount}
                      onChange={(e) => setForm((f) => ({ ...f, caching_discount: e.target.value }))}
                      className="admin-input"
                      placeholder="20"
                      dir="ltr"
                    />
                  </div>
                )}
                {form.plan_kind === "fixed" && (
                  <div>
                    <label className="block text-sm font-medium mb-1.5" style={{ color: "var(--admin-text-secondary)" }}>
                      تعرفه ثابت
                    </label>
                    <p className="text-sm py-1.5 px-2 rounded-lg" style={{ background: "rgba(16,185,129,0.08)", color: "#10b981" }}>
                      قیمت ثابت — بدون کارمزد پلتفرم و تخفیف کش
                    </p>
                  </div>
                )}
                <div>
                  <label className="block text-sm font-medium mb-1.5" style={{ color: "var(--admin-text-secondary)" }}>
                    سطح اولویت
                  </label>
                  <input
                    type="number"
                    min={0}
                    value={form.priority_level}
                    onChange={(e) => setForm((f) => ({ ...f, priority_level: e.target.value }))}
                    className="admin-input"
                    placeholder="3"
                    dir="ltr"
                  />
                </div>
              </div>

              {form.plan_kind === "fixed" && (
                <div className="rounded-xl p-3" style={{ background: "rgba(16,185,129,0.08)", border: "1px solid #10b981" }}>
                  <p className="text-sm font-medium mb-2" style={{ color: "#047857" }}>
                    سقف‌های تعرفه ثابت (CodeCraft)
                  </p>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium mb-1.5" style={{ color: "var(--admin-text-secondary)" }}>
                        درخواست در دقیقه
                      </label>
                      <input
                        type="number"
                        min={1}
                        value={form.requests_per_minute}
                        onChange={(e) => setForm((f) => ({ ...f, requests_per_minute: e.target.value }))}
                        className="admin-input"
                        placeholder="30"
                        dir="ltr"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium mb-1.5" style={{ color: "var(--admin-text-secondary)" }}>
                        توکن در دقیقه
                      </label>
                      <input
                        type="number"
                        min={1}
                        value={form.tokens_per_minute}
                        onChange={(e) => setForm((f) => ({ ...f, tokens_per_minute: e.target.value }))}
                        className="admin-input"
                        placeholder="300000"
                        dir="ltr"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium mb-1.5" style={{ color: "var(--admin-text-secondary)" }}>
                        توکن ماهانه
                      </label>
                      <input
                        type="number"
                        min={1}
                        value={form.monthly_token_limit}
                        onChange={(e) => setForm((f) => ({ ...f, monthly_token_limit: e.target.value }))}
                        className="admin-input"
                        placeholder="5000000"
                        dir="ltr"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium mb-1.5" style={{ color: "var(--admin-text-secondary)" }}>
                        درخواست همزمان (اختیاری)
                      </label>
                      <input
                        type="number"
                        min={1}
                        value={form.max_concurrent_requests}
                        onChange={(e) => setForm((f) => ({ ...f, max_concurrent_requests: e.target.value }))}
                        className="admin-input"
                        placeholder="1"
                        dir="ltr"
                      />
                    </div>
                  </div>
                  <div className="mt-3">
                    <label className="block text-sm font-medium mb-1.5" style={{ color: "var(--admin-text-secondary)" }}>
                      مدل‌های CodeCraft (با جداساز , یا ; — خالی = همه)
                    </label>
                    <input
                      type="text"
                      value={form.fixed_models_text}
                      onChange={(e) => setForm((f) => ({ ...f, fixed_models_text: e.target.value }))}
                      className="admin-input"
                      placeholder="codecraft-flash، codecraft-pro"
                      dir="rtl"
                    />
                  </div>
                </div>
              )}

              <div>
                <label className="block text-sm font-medium mb-2" style={{ color: "var(--admin-text-secondary)" }}>
                  مدت‌های قابل خرید و تخفیف هر مدت
                </label>
                <div className="space-y-2 rounded-xl p-3" style={{ background: "var(--admin-bg-hover)", border: "1px solid var(--admin-border)" }}>
                  {DURATION_OPTIONS.map((opt) => (
                    <div key={opt.days} className="flex items-center justify-between gap-2 px-1 py-1 rounded-lg">
                      <label
                        className="flex items-center gap-2.5 cursor-pointer text-sm"
                        style={{ color: "var(--admin-text-primary)" }}
                      >
                        <input
                          type="checkbox"
                          checked={form.durations.includes(opt.days)}
                          onChange={() => toggleDuration(opt.days)}
                          style={{ accentColor: "var(--admin-primary)" }}
                        />
                        {opt.label}
                      </label>
                      {form.durations.includes(opt.days) && (
                        <div className="flex items-center gap-1.5" dir="ltr">
                          <input
                            type="number"
                            min={0}
                            max={100}
                            value={form.discounts[opt.days] ?? 0}
                            onChange={(e) => setDiscount(opt.days, Number(e.target.value))}
                            className="admin-input w-16 px-2 py-1 text-center text-sm"
                            placeholder="0"
                          />
                          <span className="text-[11px] shrink-0" style={{ color: "var(--admin-text-tertiary)" }}>
                            ٪ تخفیف
                          </span>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium mb-2" style={{ color: "var(--admin-text-secondary)" }}>
                  نمایش پلن
                </label>
                <div className="flex items-center gap-6 rounded-xl p-3" style={{ background: "var(--admin-bg-hover)", border: "1px solid var(--admin-border)" }}>
                  <label className="flex items-center gap-2.5 cursor-pointer text-sm" style={{ color: "var(--admin-text-primary)" }}>
                    <input
                      type="radio"
                      name="plan-visibility"
                      checked={form.is_public}
                      onChange={() => setForm((f) => ({ ...f, is_public: true }))}
                      style={{ accentColor: "var(--admin-primary)" }}
                    />
                    نمایش عمومی
                  </label>
                  <label className="flex items-center gap-2.5 cursor-pointer text-sm" style={{ color: "var(--admin-text-primary)" }}>
                    <input
                      type="radio"
                      name="plan-visibility"
                      checked={!form.is_public}
                      onChange={() => setForm((f) => ({ ...f, is_public: false }))}
                      style={{ accentColor: "var(--admin-primary)" }}
                    />
                    نمایش خصوصی
                  </label>
                </div>

                {!form.is_public && (
                  <div className="mt-3 rounded-xl p-3" style={{ background: "var(--admin-bg-hover)", border: "1px solid var(--admin-border)" }}>
                    <label className="block text-sm font-medium mb-2" style={{ color: "var(--admin-text-secondary)" }}>
                      کاربران مجاز ({toPersianDigits(String(form.visible_user_ids.length))})
                    </label>

                    <input
                      type="text"
                      value={userSearch}
                      onChange={(e) => setUserSearch(e.target.value)}
                      className="admin-input mb-2"
                      placeholder="جستجوی کاربر با نام کاربری، ایمیل یا شماره تلفن..."
                    />

                    {form.visible_user_ids.length > 0 && (
                      <div className="flex flex-wrap gap-1.5 mb-2">
                        {form.visible_user_ids.map((id) => (
                          <span
                            key={id}
                            className="inline-flex items-center gap-1.5 px-2 py-1 rounded-lg text-xs"
                            style={{ background: "var(--admin-primary-light)", color: "var(--admin-primary-dark)" }}
                          >
                            {userLabel(id)}
                            <button
                              type="button"
                              onClick={() => togglePlanUser(id)}
                              className="leading-none"
                              style={{ color: "var(--admin-primary-dark)" }}
                            >
                              ✕
                            </button>
                          </span>
                        ))}
                      </div>
                    )}

                    <div
                      className="rounded-xl"
                      style={{ maxHeight: 180, overflowY: "auto" }}
                    >
                      {planUserCandidates.length === 0 ? (
                        <p className="text-xs py-2" style={{ color: "var(--admin-text-tertiary)" }}>
                          نتیجه‌ای یافت نشد
                        </p>
                      ) : (
                        planUserCandidates.map((u) => (
                          <button
                            key={u.id as number}
                            type="button"
                            onClick={() => togglePlanUser(u.id as number)}
                            className="w-full text-left px-2 py-1.5 rounded-lg text-sm transition-colors"
                            style={{ color: "var(--admin-text-primary)" }}
                            onMouseEnter={(e) => (e.currentTarget.style.background = "var(--admin-bg-hover)")}
                            onMouseLeave={(e) => (e.currentTarget.style.background = "transparent")}
                          >
                            {((u.name as string) || (u.username as string) || "") && (
                              <span className="font-medium">{(u.name as string) || (u.username as string)}{" "}</span>
                            )}
                            <span style={{ color: "var(--admin-text-tertiary)" }}>
                              {(u.email as string) || ""}
                            </span>
                            {(u.email as string) && (u.phone as string) && (
                              <span style={{ color: "var(--admin-text-tertiary)" }}>{" · "}</span>
                            )}
                            {(u.phone as string) && (
                              <span dir="ltr" style={{ color: "var(--admin-text-tertiary)" }}>
                                {u.phone as string}
                              </span>
                            )}
                          </button>
                        ))
                      )}
                    </div>
                  </div>
                )}
              </div>

              {form.plan_kind === "fixed" ? (
                <div>
                  <div className="flex items-center justify-between mb-2">
                    <label className="block text-sm font-medium" style={{ color: "var(--admin-text-secondary)" }}>
                      مدل‌های CodeCraft ({toPersianDigits(String((codecraftModels as Record<string, unknown>[] | undefined)?.length ?? 0))})
                    </label>
                    <label
                      className="flex items-center gap-2 cursor-pointer text-sm shrink-0"
                      style={{ color: "var(--admin-text-primary)" }}
                    >
                      <input
                        type="checkbox"
                        checked={Boolean(
                          (codecraftModels as Record<string, unknown>[] | undefined)?.length &&
                          (codecraftModels as Record<string, unknown>[]).every(
                            (m) => form.fixed_model_ids.includes(m.model_name as string)
                          )
                        )}
                        onChange={(e) =>
                          setForm((f) => ({
                            ...f,
                            fixed_model_ids: e.target.checked
                              ? (codecraftModels as Record<string, unknown>[]).map((m) => m.model_name as string)
                              : [],
                          }))
                        }
                        style={{ accentColor: "#10b981" }}
                      />
                      انتخاب همه
                    </label>
                  </div>
                  <div
                    className="gap-2 rounded-xl p-3"
                    style={{ background: "rgba(16,185,129,0.08)", border: "1px solid #10b981", maxHeight: 220, overflowY: "auto" }}
                  >
                    {codecraftLoading ? (
                      <p className="text-sm" style={{ color: "var(--admin-text-tertiary)" }}>در حال دریافت مدل‌ها...</p>
                    ) : (codecraftModels as Record<string, unknown>[] | undefined)?.length ? (
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-1">
                        {(codecraftModels as Record<string, unknown>[]).map((m) => (
                          <label
                            key={m.model_name as string}
                            className="flex items-center gap-2.5 px-1 py-1 rounded-lg cursor-pointer text-sm truncate"
                            style={{ color: "var(--admin-text-primary)" }}
                          >
                            <input
                              type="checkbox"
                              checked={form.fixed_model_ids.includes(m.model_name as string)}
                              onChange={() => toggleCodecraftModel(m.model_name as string)}
                              style={{ accentColor: "#10b981" }}
                            />
                            <span className="truncate" title={(m.model_name as string)}>
                              {(m.display_name as string) || (m.model_name as string)}
                            </span>
                          </label>
                        ))}
                      </div>
                    ) : (
                      <p className="text-sm" style={{ color: "var(--admin-text-tertiary)" }}>
                        {codecraftError
                          ? "دریافت مدل‌های CodeCraft ناموفق بود — میتوانید آیدی مدل‌ها را دستی وارد کنید"
                          : "مدلی دریافت نشد — میتوانید آیدی مدل‌ها را دستی وارد کنید"}
                      </p>
                    )}
                  </div>
                  <div className="mt-3">
                    <label className="block text-sm font-medium mb-1.5" style={{ color: "var(--admin-text-secondary)" }}>
                      آیدی مدل‌های اضافی (اختیاری — با جداساز , یا ;)
                    </label>
                    <input
                      type="text"
                      value={form.fixed_models_text}
                      onChange={(e) => setForm((f) => ({ ...f, fixed_models_text: e.target.value }))}
                      className="admin-input"
                      placeholder="claude-opus-4.8، claude-fable-5"
                      dir="ltr"
                    />
                  </div>
                </div>
              ) : (
                <div>
                  <div className="flex items-center justify-between mb-2">
                    <label className="block text-sm font-medium" style={{ color: "var(--admin-text-secondary)" }}>
                      مدل‌های شامل ({toPersianDigits(String(activeModels.length))})
                    </label>
                    <label
                      className="flex items-center gap-2 cursor-pointer text-sm shrink-0"
                      style={{ color: "var(--admin-text-primary)" }}
                    >
                      <input
                        type="checkbox"
                        checked={activeModels.length > 0 && activeModels.every((m) => form.model_ids.includes(m.id as number))}
                        onChange={(e) =>
                          setForm((f) => ({
                            ...f,
                            model_ids: e.target.checked
                              ? activeModels.map((m) => m.id as number)
                              : [],
                          }))
                        }
                        style={{ accentColor: "var(--admin-primary)" }}
                      />
                      انتخاب همه
                    </label>
                  </div>
                  <div
                    className="gap-2 rounded-xl p-3"
                    style={{ background: "var(--admin-bg-hover)", border: "1px solid var(--admin-border)", maxHeight: 220, overflowY: "auto" }}
                  >
                    {!activeModels.length ? (
                      <p className="text-sm" style={{ color: "var(--admin-text-tertiary)" }}>هنوز مدلی ثبت نشده است</p>
                    ) : (
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-1">
                        {activeModels.map((m) => (
                          <label
                            key={m.id as number}
                            className="flex items-center gap-2.5 px-1 py-1 rounded-lg cursor-pointer text-sm truncate"
                            style={{ color: "var(--admin-text-primary)" }}
                          >
                            <input
                              type="checkbox"
                              checked={form.model_ids.includes(m.id as number)}
                              onChange={() => toggleModel(m.id as number)}
                              style={{ accentColor: "var(--admin-primary)" }}
                            />
                            <span className="truncate" title={(m.model_name as string)}>
                              {(m.display_name as string) || (m.model_name as string)}
                            </span>
                          </label>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
              )}

              <div>
                <label className="flex items-center gap-2.5 cursor-pointer text-sm" style={{ color: "var(--admin-text-primary)" }}>
                  <input
                    type="checkbox"
                    checked={form.is_active}
                    onChange={(e) => setForm((f) => ({ ...f, is_active: e.target.checked }))}
                    style={{ accentColor: "var(--admin-primary)" }}
                  />
                  پلن فعال است
                </label>
              </div>

              <div className="flex gap-3 pt-2">
                <button onClick={() => setShowForm(false)} className="admin-btn admin-btn-secondary flex-1">
                  انصراف
                </button>
                <button
                  onClick={handleSave}
                  disabled={createPlan.isPending || updatePlan.isPending || !form.name.trim() || (form.plan_kind === "fixed" ? form.fixed_price_usd === "" : form.monthly_price === "")}
                  className="admin-btn admin-btn-primary flex-1"
                >
                  ذخیره
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
